import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';

import BusyOverlay from '@bidding/shared/components/BusyOverlay/BusyOverlay';
import useAuth from '../hooks/useAuth';
import { extractErrorMessage } from '../helpers/errorMessage';

// 4-step flow — mirrors `Front/v5/js/auth/components/ResetPasswordPage.js`
// and `Front/resume/js/auth/components/ResumeResetPassword.js`:
//
//   1. Email — request a reset token, stash email in state.
//   2. Code — customer pastes the 6-digit code from their inbox.
//   3. New password — set + confirm, submitted along with email + code.
//   4. Done — success screen with a "Sign in" CTA.
//
// Visuals rebuilt on the same v2 card chrome as `LoginForm` /
// `RegisterForm` — same `.bidding-auth2-*` namespace. Legacy
// `.bidding-order-modal.reset` markup is gone.
const STEP_EMAIL = 'email';
const STEP_CODE = 'code';
const STEP_NEW_PASSWORD = 'new';
const STEP_DONE = 'done';

// ── Shared primitives (same as LoginForm) ───────────────────────────────
function IconInput({ name, type = 'text', placeholder, icon, trailing, required, autoFocus }) {
    return (
        <Field name={name}>
            {({ input, meta }) => {
                const showError = meta.touched && meta.error;
                return (
                    <>
                        <span className={`bidding-auth2-input${showError ? ' has-error' : ''}`}>
                            {icon && (
                                <span className="bidding-auth2-input__ico" aria-hidden="true">
                                    {icon}
                                </span>
                            )}
                            <input
                                {...input}
                                type={type}
                                placeholder={placeholder}
                                autoComplete={
                                    type === 'password' ? 'new-password'
                                    : type === 'email' ? 'email'
                                    : 'off'
                                }
                                aria-required={required || undefined}
                                autoFocus={autoFocus}
                            />
                            {trailing}
                        </span>
                        {showError && (
                            <span className="bidding-auth2-field__error">{meta.error}</span>
                        )}
                    </>
                );
            }}
        </Field>
    );
}

function EyeButton({ shown, onToggle }) {
    return (
        <button
            type="button"
            className="bidding-auth2-input__eye"
            onClick={onToggle}
            aria-label={shown ? 'Hide password' : 'Show password'}
            tabIndex={-1}
        >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {shown
                    ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><path d="M1 1l22 22"/></>
                    : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>}
            </svg>
        </button>
    );
}

const EmailIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
);

const KeyIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m21 2-9.6 9.6"/><circle cx="7.5" cy="15.5" r="5.5"/><path d="m15 6 3 3"/><path d="m14 7 3 3"/></svg>
);

const CodeIcon = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
);

// Small 3-dot step indicator. Lives under the kicker so the customer
// knows where they are in the flow.
function StepDots({ step }) {
    const order = [STEP_EMAIL, STEP_CODE, STEP_NEW_PASSWORD];
    const activeIdx = order.indexOf(step);
    return (
        <div className="bidding-auth2-steps" aria-hidden="true">
            {order.map((s, i) => (
                <span
                    key={s}
                    className={
                        `bidding-auth2-steps__dot${i === activeIdx ? ' is-active' : ''}`
                        + (i < activeIdx ? ' is-done' : '')
                    }
                />
            ))}
        </div>
    );
}

// ── Individual step forms ───────────────────────────────────────────────
function EmailStep({ onNext, onBack, initialEmail }) {
    const { submitResetRequest } = useAuth();
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (values) => {
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        try {
            await submitResetRequest({ email: values.email });
            onNext(values.email);
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="bidding-auth2-card">
            <span className="bidding-auth2-card__kicker">Step 1 of 3</span>
            <h2 className="bidding-auth2-card__title">Reset your password</h2>
            <p className="bidding-auth2-card__subtitle">
                Enter the email you signed up with. We&rsquo;ll send you a 6-digit
                code to reset your password.
            </p>
            <StepDots step={STEP_EMAIL} />
            <Form
                onSubmit={onSubmit}
                initialValues={{ email: initialEmail || '' }}
                validate={(values) => {
                    const errors = {};
                    if (!values.email) errors.email = 'Please enter your email.';
                    else if (!/.+@.+\..+/.test(values.email)) errors.email = 'That doesn’t look like a valid email.';
                    return errors;
                }}
            >
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="bidding-auth2-form">
                        <label className="bidding-auth2-field">
                            <span className="bidding-auth2-field__label">Email</span>
                            <IconInput
                                name="email"
                                type="email"
                                placeholder="you@example.com"
                                icon={EmailIcon}
                                required
                                autoFocus
                            />
                        </label>

                        <button
                            type="submit"
                            className="bidding-auth2-submit"
                            disabled={submitting}
                        >
                            {submitting ? 'Sending…' : 'Send code'}
                        </button>

                        <div className="bidding-auth2-error-slot" aria-live="polite">
                            {error && (
                                <div className="bidding-auth2-error">{error}</div>
                            )}
                        </div>
                    </form>
                )}
            </Form>

            <div className="bidding-auth2-card__foot">
                Remembered it?{' '}
                <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onBack(); }}
                >
                    Back to login
                </a>
            </div>

            {submitting && <BusyOverlay message="Sending reset email" />}
        </section>
    );
}

function CodeStep({ email, onNext, onBackToEmail, onBack }) {
    const { submitResetCode, submitResetRequest } = useAuth();
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);
    const [resentAt, setResentAt] = useState(null);

    const onSubmit = async (values) => {
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        try {
            await submitResetCode({ email, code: values.code });
            onNext(values.code);
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    const onResend = async () => {
        if (resending) return;
        setResending(true);
        setError(null);
        try {
            await submitResetRequest({ email });
            setResentAt(Date.now());
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setResending(false);
        }
    };

    return (
        <section className="bidding-auth2-card">
            <span className="bidding-auth2-card__kicker">Step 2 of 3</span>
            <h2 className="bidding-auth2-card__title">Check your inbox</h2>
            <p className="bidding-auth2-card__subtitle">
                We sent a 6-digit code to <strong>{email}</strong>. Paste it
                below to continue.{' '}
                <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onBackToEmail(); }}
                    className="bidding-auth2-card__sublink"
                >
                    Change email
                </a>
            </p>
            <StepDots step={STEP_CODE} />
            <Form
                onSubmit={onSubmit}
                validate={(values) => {
                    const errors = {};
                    if (!values.code) errors.code = 'Please enter the code.';
                    else if (String(values.code).length !== 6) errors.code = 'The code is 6 digits.';
                    return errors;
                }}
            >
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="bidding-auth2-form">
                        <label className="bidding-auth2-field">
                            <span className="bidding-auth2-field__label">
                                Verification code
                                <a
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); onResend(); }}
                                >
                                    {resending ? 'Sending…' : resentAt ? 'Sent ✓' : 'Resend'}
                                </a>
                            </span>
                            <IconInput
                                name="code"
                                type="text"
                                placeholder="123456"
                                icon={CodeIcon}
                                required
                                autoFocus
                            />
                        </label>

                        <button
                            type="submit"
                            className="bidding-auth2-submit"
                            disabled={submitting}
                        >
                            {submitting ? 'Verifying…' : 'Verify code'}
                        </button>

                        <div className="bidding-auth2-error-slot" aria-live="polite">
                            {error && (
                                <div className="bidding-auth2-error">{error}</div>
                            )}
                        </div>
                    </form>
                )}
            </Form>

            <div className="bidding-auth2-card__foot">
                <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onBack(); }}
                >
                    ← Back to login
                </a>
            </div>

            {submitting && <BusyOverlay message="Verifying code" />}
        </section>
    );
}

function NewPasswordStep({ email, code, onDone, onBack }) {
    const { submitResetNewPassword } = useAuth();
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const onSubmit = async (values) => {
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        try {
            await submitResetNewPassword({
                email,
                code,
                newPassword: values.newPassword,
                confirmPassword: values.confirmPassword,
            });
            onDone();
        } catch (e) {
            setError(extractErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="bidding-auth2-card">
            <span className="bidding-auth2-card__kicker">Step 3 of 3</span>
            <h2 className="bidding-auth2-card__title">Set a new password</h2>
            <p className="bidding-auth2-card__subtitle">
                Pick something secure (at least 5 characters). You&rsquo;ll use
                this to sign in next time.
            </p>
            <StepDots step={STEP_NEW_PASSWORD} />
            <Form
                onSubmit={onSubmit}
                validate={(values) => {
                    const errors = {};
                    if (!values.newPassword) errors.newPassword = 'Please pick a password.';
                    else if (values.newPassword.length < 5) errors.newPassword = 'Use at least 5 characters.';
                    if (!values.confirmPassword) errors.confirmPassword = 'Please confirm your password.';
                    else if (values.newPassword && values.confirmPassword !== values.newPassword) {
                        errors.confirmPassword = 'Doesn’t match.';
                    }
                    return errors;
                }}
            >
                {({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="bidding-auth2-form">
                        <label className="bidding-auth2-field">
                            <span className="bidding-auth2-field__label">New password</span>
                            <IconInput
                                name="newPassword"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="At least 5 characters"
                                icon={KeyIcon}
                                required
                                autoFocus
                                trailing={
                                    <EyeButton
                                        shown={showPassword}
                                        onToggle={() => setShowPassword(v => !v)}
                                    />
                                }
                            />
                        </label>

                        <label className="bidding-auth2-field">
                            <span className="bidding-auth2-field__label">Confirm new password</span>
                            <IconInput
                                name="confirmPassword"
                                type={showConfirm ? 'text' : 'password'}
                                placeholder="Type it again"
                                icon={KeyIcon}
                                required
                                trailing={
                                    <EyeButton
                                        shown={showConfirm}
                                        onToggle={() => setShowConfirm(v => !v)}
                                    />
                                }
                            />
                        </label>

                        <button
                            type="submit"
                            className="bidding-auth2-submit"
                            disabled={submitting}
                        >
                            {submitting ? 'Saving…' : 'Save and sign in'}
                        </button>

                        <div className="bidding-auth2-error-slot" aria-live="polite">
                            {error && (
                                <div className="bidding-auth2-error">{error}</div>
                            )}
                        </div>
                    </form>
                )}
            </Form>

            <div className="bidding-auth2-card__foot">
                <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); onBack(); }}
                >
                    ← Back to login
                </a>
            </div>

            {submitting && <BusyOverlay message="Updating password" />}
        </section>
    );
}

function DoneStep({ onBack }) {
    return (
        <section className="bidding-auth2-card bidding-auth2-card--success">
            <div className="bidding-auth2-success" aria-hidden="true">
                <svg viewBox="0 0 48 48" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="24" cy="24" r="21" />
                    <path d="M14 24l7 7 14-14" />
                </svg>
            </div>
            <h2 className="bidding-auth2-card__title">Password updated</h2>
            <p className="bidding-auth2-card__subtitle">
                Your new password is ready. Sign in with it next time.
            </p>
            <button
                type="button"
                className="bidding-auth2-submit"
                onClick={onBack}
            >
                Sign in
            </button>
        </section>
    );
}

// ── Flow controller ─────────────────────────────────────────────────────
export default function ResetPasswordFlow({ onBack }) {
    const [step, setStep] = useState(STEP_EMAIL);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');

    if (step === STEP_EMAIL) {
        return (
            <EmailStep
                initialEmail={email}
                onBack={onBack}
                onNext={(e) => { setEmail(e); setStep(STEP_CODE); }}
            />
        );
    }
    if (step === STEP_CODE) {
        return (
            <CodeStep
                email={email}
                onBack={onBack}
                onBackToEmail={() => setStep(STEP_EMAIL)}
                onNext={(c) => { setCode(c); setStep(STEP_NEW_PASSWORD); }}
            />
        );
    }
    if (step === STEP_NEW_PASSWORD) {
        return (
            <NewPasswordStep
                email={email}
                code={code}
                onBack={onBack}
                onDone={() => setStep(STEP_DONE)}
            />
        );
    }
    return <DoneStep onBack={onBack} />;
}
